import { createFileRoute } from "@tanstack/react-router";
import { SiteShell } from "@/components/site/SiteShell";
import { OutcomeCaseStudyArticle } from "@/components/resources/OutcomeCaseStudyArticle";
import { buildHead, breadcrumbJsonLd } from "@/lib/seo";
import { articleJsonLdForPillar } from "@/components/site/PillarArticle";
import { setEdgeHtmlCache } from "@/lib/cache-headers";
import body from "../../content/resources/mp-health-squarespace-shopify-migration-case-study.en.md?raw";

const PATH = "/resources/mp-health-squarespace-migration-case-study";
const ALT = "/es/recursos/caso-mp-health-migracion-squarespace";
const TITLE = "MP Health: Squarespace to Shopify with a Custom Medical Inventory System";
const META_TITLE = "MP Health Squarespace to Shopify Case Study · Shugert";
const DESCRIPTION = "How MP Health moved from Squarespace to Shopify in 4–6 weeks with component-level IV inventory logic, Shopify POS, local SEO and mobile UX improvements.";
const PUBLISHED = "2026-08-20T14:30:00.000Z";
const MODIFIED = "2026-08-20T14:30:00.000Z";

const SOURCES = [
  { label: "Shugert Marketing partner profile", publisher: "Shopify", href: "https://www.shopify.com/partners/directory/partner/shugert-marketing" },
  { label: "MP Health", publisher: "MP Health", href: "https://mphealth.net/" },
  { label: "Shopify POS", publisher: "Shopify Help Center", href: "https://help.shopify.com/en/manual/sell-in-person" },
  { label: "Managing inventory", publisher: "Shopify Help Center", href: "https://help.shopify.com/en/manual/products/inventory" },
];

const RELATED = [
  { label: "Shopify development", href: "/shopify-experts" },
  { label: "Shopify migration", href: "/shopify-migration" },
  { label: "Shopify technical SEO", href: "/shopify-technical-seo" },
  { label: "All Shopify case studies", href: "/work" },
];

export const Route = createFileRoute("/resources/mp-health-squarespace-migration-case-study")({
  loader: async () => { await setEdgeHtmlCache(); },
  head: () => buildHead({
    locale: "en", title: META_TITLE, description: DESCRIPTION, path: PATH, altPath: ALT, type: "article",
    author: "Samuel Noriega", articleAuthor: "Samuel Noriega", articleSection: "Shopify systems case study", publishedTime: PUBLISHED, modifiedTime: MODIFIED,
    jsonLd: [
      articleJsonLdForPillar({ title: TITLE, description: DESCRIPTION, path: PATH, publishedTime: PUBLISHED, modifiedTime: MODIFIED, section: "Shopify systems case study" }),
      breadcrumbJsonLd([{ name: "Home", path: "/" }, { name: "Work", path: "/work" }, { name: "MP Health case study", path: PATH }]),
    ],
  }),
  component: () => (
    <SiteShell>
      <OutcomeCaseStudyArticle
        locale="en" path={PATH} title={TITLE}
        excerpt="How a medical clinic moved to Shopify and connected each sellable IV treatment to the multiple inventory components it consumes, with POS, local discovery, and mobile UX in the same scope."
        body={body} updated="August 2026" publishedAt={PUBLISHED} modifiedAt={MODIFIED} readingMinutes={10}
        focus="How Shopify was adapted to a service business where one purchased treatment consumes several underlying inventory components and transactions also happen in clinic."
        audience="Service businesses and specialty merchants evaluating Shopify where standard one-product/one-stock-unit inventory does not match the real operating model."
        sources={SOURCES} related={RELATED}
        category="Shopify custom systems case study" backLabel="← Work" backHref="/work"
        cta={{ eyebrow: "Commerce that matches operations", title: "Model the business rule before choosing the app stack.", body: "We scope the inventory relationship, transaction channels, system ownership, failure cases, and storefront requirements before custom Shopify development begins.", label: "Request a systems scope", href: "/get-a-quote?intent=custom&source=mp-health-case-study" }}
      />
    </SiteShell>
  ),
});
